<template>
  <div>
    <job-lead-form @change="handleLeadChange"></job-lead-form>
    <divider></divider>
    <div :class="['field-wrapper-columns', { 'd-flex': jobStatusEnabled }]">
      <service-category-form
        ref="service_category_form"
        v-model="formData.service_category_id"
      >
      </service-category-form>
      <job-status-form
        v-if="jobStatusEnabled"
        v-model="formData.job_status_id"
        wrapperClass="ml-3"
      ></job-status-form>
    </div>
    <divider></divider>
    <eligible-technicians-form
      v-model="formData.user_id"
      :service-category-id="formData.service_category_id"
      @update-pinned-reason="setPinnedReason"
    ></eligible-technicians-form>
    <divider></divider>
    <availability-form
      v-if="formData.service_category_id"
      :scheduled-time="formData.scheduled_time"
      :arrival_window_start="formData.arrival_window_start"
      :arrival_window_end="formData.arrival_window_end"
      :service-category-id="formData.service_category_id"
      :technician-id="primaryTechId"
      :technician-ids="multiTechUserIds"
      :timezone="address.time_zone"
      :postal-code="address.zip_code"
      :current-scheduled-time="formData.scheduled_time"
      :default-day="new Date().toString()"
      data-vv-name="Scheduled"
      allow-do-not-notify
      @unavailable="setUnavailable"
      @do-not-notify="setDoNotNotify"
      @update:data="updateData"
    ></availability-form>
    <div v-else>
      <b>Scheduled:</b>
      <span class="hint-text"
        >Select a Service Category to View Availability</span
      >
    </div>
    <divider></divider>
    <text-area
      v-model="formData.notes"
      label="Appointment Notes"
      class="full-width"
    ></text-area>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import Divider from "@/components/Divider.vue";
import TextArea from "@/components/TextArea.vue";
import AvailabilityForm from "@/EditAppointmentModal/forms/AvailabilityForm.vue";
import EligibleTechniciansForm from "@/EditAppointmentModal/forms/EligibleTechniciansForm.vue";
import JobStatusForm from "@/EditAppointmentModal/forms/JobStatusForm.vue";
import ServiceCategoryForm from "@/EditAppointmentModal/forms/ServiceCategoryForm.vue";
import JobLeadForm from "@/ScheduleModal/JobLeadForm.vue";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    Divider,
    EligibleTechniciansForm,
    AvailabilityForm,
    ServiceCategoryForm,
    JobStatusForm,
    TextArea,
    JobLeadForm
  },
  props: {
    address: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        customer_id: this.address.owner_id,
        address_id: this.address.id,
        lead_type: null,
        lead_source: null,
        service_category_id: null,
        job_status_id: null,
        scheduled_time: "",
        user_id: null,
        notes: "",
        unavailable_time: false,
        skip_notifications: false
      }
    };
  },
  computed: {
    ...mapState({
      serviceCategories: "serviceCategories",
      selectedServiceCategory({ serviceCategories }) {
        return serviceCategories.find(
          (sc) => sc.id === this.formData.service_category_id
        );
      }
    }),
    ...mapGetters(["jobStatusEnabled"]),
    isMultiTechEnabled() {
      return !!this.$store.state.tenant?.feature_flags
        ?.multiple_technicians_ff_enabled;
    },
    primaryTechId() {
      const uid = this.formData.user_id;
      if (Array.isArray(uid)) return uid[0] ?? null;
      return uid;
    },
    multiTechUserIds() {
      if (!this.isMultiTechEnabled) return undefined;
      const uid = this.formData.user_id;
      if (Array.isArray(uid)) return uid;
      if (uid != null) return [uid];
      return undefined;
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  methods: {
    userIdsToTechnicianProfileIds(userIds) {
      const technicians = this.$store.state.technicians || [];
      const ids = Array.isArray(userIds)
        ? userIds
        : userIds != null
        ? [userIds]
        : [];
      return ids
        .map(
          (uid) =>
            technicians.find((t) => t.user_id === uid)?.technician_profile_id
        )
        .filter((id) => id != null);
    },
    getFormData() {
      const {
        service_category_id,
        scheduled_time,
        arrival_window_start,
        arrival_window_end,
        user_id,
        notes,
        unavailable_time,
        pinned_reason,
        skip_notifications,
        ...jobParams
      } = this.formData;
      const multiTechEnabled =
        this.$store.state.tenant?.feature_flags
          ?.multiple_technicians_ff_enabled;
      const appointment = {
        service_category_id,
        scheduled_time,
        arrival_window_start,
        arrival_window_end,
        notes,
        unavailable_time,
        pinned_reason,
        skip_notifications
      };
      if (multiTechEnabled) {
        appointment.technician_profile_ids =
          this.userIdsToTechnicianProfileIds(user_id);
      } else {
        appointment.user_id = user_id;
      }
      return {
        ...jobParams,
        appointment
      };
    },
    async validate() {
      const serviceCategoryValid =
        await this.$refs.service_category_form.validate();
      return serviceCategoryValid;
    },
    handleLeadChange(e) {
      const { value, name } = e;
      this.formData[name] = value;
    },
    setUnavailable(unavailable) {
      this.formData.unavailable_time = unavailable;
    },
    setDoNotNotify(doNotNotify) {
      this.formData.skip_notifications = doNotNotify;
    },
    setPinnedReason(val) {
      this.formData.pinned_reason = val;
    },
    updateData({ key, value }) {
      this.formData[key] = value;
    }
  }
};
</script>

<style lang="scss" scoped>
/* BOLDING TEXTAREA LABEL */
:deep(.label) {
  font-weight: bold;
}
.field-wrapper-columns > * {
  flex-grow: 1;
}
</style>
